import { computed, ref } from 'vue'

export type Locale = 'en' | 'ko'

const savedLocale = localStorage.getItem('wave-locale')
const initialLocale: Locale = savedLocale === 'ko' || savedLocale === 'en'
  ? savedLocale
  : navigator.language.toLowerCase().startsWith('ko') ? 'ko' : 'en'

const locale = ref<Locale>(initialLocale)

const messages: Record<Locale, Record<string, string>> = {
  en: {
    'playground.title': "Playground",
    'playground.intro': "Write and run Wave in your browser. Edit the example below to get started.",
    'playground.stdin': "Standard input",
    'playground.stdinHelp': "Input is supplied before execution. Reading past the end returns EOF.",
    'playground.run': "Run",
    'playground.stop': "Stop",
    'playground.open': "Open playground",
    'playground.output': "Output",
    'playground.terminal': "Terminal",
    'playground.error': "Error",
    'playground.idle': "Ready",
    'playground.compile': "Compiling…",
    'playground.running': "Running…",
    'playground.done': "Finished",
    'playground.stopped': "Stopped",
    'playground.exit': "Exit code",
    'playground.noOutput': "The program finished without output.",
    'playground.limits': "3 seconds · 64 MiB memory · 32 KiB source. File and network access are unavailable.",
    'playground.compileTimeout': "Compilation timed out. Please try again.",
    'playground.runTimeout': "Execution stopped after 3 seconds.",
    'playground.failed': "The program could not be run.",
    'playground.reset': "Reset",
    'playground.options': "Input and execution details",
    'home.languageTitle': 'Wave programming language',
    'home.languageIntro': 'A general-purpose language built as a modern evolution of C, with native compilation, explicit types, and direct control for applications, command-line tools, and systems software.',
    'home.developmentStatus': 'Wave is in active pre-beta development. Syntax and toolchain contracts may change between releases.',
    'home.exampleTitle': 'Your first Wave program',
    'home.exampleSave': 'Save this complete program as main.wave:',
    'home.exampleRun': 'After installing Wave, run this command in the directory containing main.wave:',
    'home.exampleOutput': 'Output:',
    'community.welcomeTitle': 'Welcome to the Wave community',
    'community.welcomeBody': 'Introduce yourself, share what you are building, or discuss Wave. For help, include your operating system, Wave version, a small example, and the full error message.',
    'community.askQuestion': 'Ask a question',
    'community.contribute': 'Contribute to Wave',
    'nav.toolchains': 'Toolchains',
    'brand.subtitle': 'Platform',
    'nav.docs': 'Documentation',
	'nav.blog': 'Blog',
    'nav.home': 'Home',
    'nav.source': 'Source',
    'nav.community': 'Community',
    'nav.personal': 'LunaStev',
    'nav.admin': 'Administration',
    'nav.questions': 'Questions',
	'nav.rfc': 'RFCs',
	'nav.explore': 'Explore',
	'nav.discuss': 'Discuss',
	'nav.workspace': 'Workspace',
	'nav.manage': 'Manage',
	'editor.toolbar': 'WaveEditor formatting',
	'editor.status': 'WaveEditor document status',
	'editor.bold': 'Bold',
	'editor.italic': 'Italic',
	'editor.inlineCode': 'Inline code',
	'editor.heading': 'Heading',
	'editor.quote': 'Quote',
	'editor.list': 'List',
	'editor.link': 'Link',
	'editor.characters': 'chars',
	'editor.words': 'words',
	'editor.lines': 'lines',
	'editor.waveCore': 'Wave core',
	'editor.compatibility': 'Wave compatible',
    'nav.mail': 'Mail',
    'nav.github': 'GitHub',
    'nav.discord': 'Discord community',
	'notifications.title': 'Notifications',
	'notifications.markAllRead': 'Mark all read',
	'notifications.loading': 'Loading notifications…',
	'notifications.failed': 'Notifications could not be loaded.',
	'notifications.empty': 'You have no notifications yet.',
	'notifications.unread': 'Unread',
	'notifications.kind.blog.comment': 'New blog comment',
	'notifications.kind.community.reply': 'New discussion reply',
	'notifications.kind.question.answer': 'New answer',
	'notifications.kind.question.accepted': 'Answer accepted',
	'notifications.kind.rfc.comment': 'New RFC comment',
	'notifications.kind.rfc.status': 'RFC status changed',
    'nav.menu': 'Toggle navigation',
    'nav.menuButton': 'Menu',
    'nav.primary': 'Primary navigation',
    'nav.language': 'Language',
    'theme.label': 'Theme',
    'theme.system': 'System theme',
    'theme.light': 'Light theme',
    'theme.black': 'Black theme',
    'theme.switchLight': 'Switch to light theme',
    'theme.switchBlack': 'Switch to black theme',
    'nav.search': 'Search Wave Platform',
    'nav.searchButton': 'Search',
    'search.scope.portal': 'Search Wave Platform',
    'search.scope.community': 'Search community',
    'search.scope.docs': 'Search documentation',
    'search.scope.questions': 'Search questions',
    'search.scope.mail': 'Search mail',
    'search.scope.source': 'Search repositories',
    'search.scope.account': 'Search Wave Platform',
    'auth.signIn': 'Sign in',
    'auth.signUp': 'Create account',
	'auth.signOut': 'Sign out',
    'auth.account': 'Account',
    'auth.username': 'Username or email',
    'auth.displayName': 'Display name',
	'auth.waveAddress': 'Wave mail address',
	'auth.waveAddressHelp': 'Your unique Wave address is generated from the display name.',
	'auth.duplicateNameAddressHelp': 'That generated address is already in use. Choose a different unique address for this duplicate display name.',
	'auth.addressChoiceRequired': 'Choose a different Wave mail address for this duplicate display name.',
    'auth.email': 'Email',
    'auth.notAvailable': 'Account registration and sign-in are not available yet.',
    'auth.submitSignIn': 'Sign in',
    'auth.submitSignUp': 'Create account',
	'auth.failed': 'The account request failed.',
	'auth.registrationClosed': 'Account registration is closed.',
	'auth.authenticatorCode': '6-digit code from your authenticator app',
	'auth.recoveryEmail': 'Recovery email',
	'auth.recoveryEmailHelp': 'Used only to recover your authenticator. A confirmation message will be sent.',
	'auth.setupAuthenticator': 'Continue to authenticator',
	'auth.connectAuthenticator': 'Connect an authenticator app',
	'auth.totpUnavailable': 'Authenticator sign-in is not configured on this server.',
	'auth.scanAuthenticator': 'Scan this QR code with an authenticator app, then enter its six-digit code.',
	'auth.scanNewAuthenticator': 'Scan this new QR code, then enter its six-digit code.',
	'auth.qrCode': 'Authenticator setup QR code',
	'auth.manualKey': 'Enter a setup key instead',
	'auth.finishAccount': 'Create account',
	'auth.lostAuthenticator': 'Lost your authenticator?',
	'auth.recoveryTitle': 'Recover account',
	'auth.recoveryHelp': 'We will send a one-time reset link to the verified recovery address.',
	'auth.sendRecovery': 'Send recovery link',
	'auth.recoverySent': 'If the account has a verified recovery address, a reset link has been sent.',
	'auth.checkSpam': 'If the email does not arrive, check your spam folder.',
	'auth.backToSignIn': 'Back to sign in',
	'auth.recoveryExpired': 'This recovery link is invalid or expired.',
	'auth.resetAuthenticator': 'Reset authenticator',
	'auth.resetComplete': 'The authenticator was replaced. Sign in with the new code.',
	'auth.verifyRecovery': 'Verify recovery email',
	'auth.recoveryVerified': 'The recovery email has been verified.',
	'auth.verificationExpired': 'This verification link is invalid or expired.',
	'auth.securitySettings': 'Security settings',
	'auth.webhookHelp': 'Connect your Wave account to Discord or an external Wave service. You can create up to 10 HTTPS endpoints.',
	'auth.webhookSaved': 'The webhook was saved.',
	'auth.webhookTestSent': 'The webhook test was delivered.',
	'auth.settings': 'Settings',
	'auth.authenticator': 'Authenticator',
	'auth.authenticatorStatus': 'TOTP sign-in:',
	'auth.currentCode': 'Current authenticator code',
	'auth.newCode': 'New authenticator code',
	'auth.replaceAuthenticator': 'Replace authenticator',
	'auth.confirmReplacement': 'Confirm replacement',
	'auth.newRecoveryEmail': 'New recovery email',
	'auth.sendVerification': 'Send verification email',
	'auth.verificationSent': 'A verification link has been sent to the new address.',
	'auth.verified': 'Verified',
	'auth.unverified': 'Not verified',
    'footer.official': 'Official Wave Foundation website',
    'footer.admin': 'Administration',
    'home.title': 'Wave Platform',
    'home.intro': 'The official portal for Wave documentation, source, releases, community, questions, and mail.',
    'home.services.title': 'Services',
    'home.about.title': 'About Wave',
    'home.about.body': 'Wave is designed for native programs, system tools, and software close to the hardware. It favors explicit types and predictable compilation.',
    'home.start.title': 'Getting started',
    'home.start.install': 'Install the latest compiler on a supported Unix-like system:',
    'home.start.example': 'Create a file named hello.wave:',
    'home.start.docs': 'Read the installation and language documentation',
    'home.news.title': 'News',
    'home.news.archive': 'Wave v0.1.9-pre-beta release notes',
    'home.download.title': 'Download',
    'home.download.body': 'Official packages and release notes are published on GitHub.',
    'home.project.title': 'Project links',
    'home.account.title': 'Wave account',
    'home.release.title': 'Release archive',
    'home.releaseNotes': 'Read release notes',
    'home.stats.title': 'Platform activity',
    'home.stats.accounts': 'Wave accounts',
    'home.stats.messages': 'Messages today',
    'home.stats.mirrors': 'Active source mirrors',
    'home.start.eyebrow': 'Wave toolchain',
    'home.releaseLabel': 'Release',
    'home.githubReleases': 'GitHub releases',
    'home.compiler': 'Wave compiler',
    'home.releaseArchive': 'Release archive',
    'home.communityLatest': 'Latest community discussions',
    'home.docsStart': 'Start with the documentation',
    'home.installWave': 'Install the Wave toolchain',
    'home.installPlatform': 'Installation platform',
    'home.latestReleases': 'Latest releases',
    'home.copyInstall': 'Copy install command',
    'home.recentSource': 'Recently updated source',
    'home.sourceEmpty': 'Source mirror data is not available yet.',
    'home.personalEmpty': 'No writing or development logs have been published yet.',
    'home.unansweredQuestions': 'Questions waiting for answers',
	'home.sponsors': 'Sponsors',
	'home.sponsorProject': 'Support Wave',
	'home.noSponsors': 'Become the first sponsor of Wave.',
	'home.oneTimeSupporters': 'One-time supporters',
    'search.title': 'Search',
    'search.lead': 'Search documentation, community posts, questions, mail, and source.',
    'search.empty': 'Search is not available yet.',
    'docs.title': 'Documentation',
    'docs.lead': 'Language guides and reference material.',
    'docs.gettingStarted': 'Getting started',
    'docs.gettingStarted.detail': 'Installation, the first program, and wavec basics',
    'docs.language': 'Learn Wave',
    'docs.language.detail': 'Types, control flow, structures, generics, and FFI',
    'docs.standardLibrary': 'Standard library',
    'docs.standardLibrary.detail': 'Strings, files, memory, networking, and system APIs',
    'docs.compiler': 'Compiler',
    'docs.compiler.detail': 'Build options, targets, diagnostics, and ABI',
    'docs.ecosystem': 'Ecosystem',
    'docs.ecosystem.detail': 'Vex, Whale, WSON, and related projects',
	'blog.official': 'Official publication',
	'blog.title': 'Wave Blog',
	'blog.lead': 'Release announcements, engineering decisions, and news from the Wave project.',
	'blog.empty': 'No blog posts have been published yet.',
	'blog.back': 'Back to the blog',
	'blog.categories': 'Blog categories',
	'blog.category.all': 'All',
	'blog.category.article': 'Articles',
	'blog.category.release': 'Releases',
	'blog.category.roadmap': 'Roadmap',
	'blog.release.title': 'Wave Releases',
	'blog.release.lead': 'Published Wave versions, release dates, and the changes included in each version.',
	'blog.release.archive': 'Wave release archive',
	'blog.release.version': 'Version',
	'blog.release.date': 'Release date',
	'blog.release.back': 'Back to releases',
	'blog.release.empty': 'No Wave releases have been published yet.',
	'blog.release.notFound': 'The requested Wave release was not found.',
	'blog.roadmap.planned': 'Planned',
	'blog.roadmap.inProgress': 'In progress',
	'blog.roadmap.released': 'Released',
	'blog.roadmap.target': 'Planned release',
	'blog.roadmap.empty': 'No roadmap entries have been published yet.',
	'blog.editor.title': 'Blog editor',
	'blog.editor.lead': 'Write and preview official Wave articles, releases, and roadmap entries with WaveEditor.',
	'blog.editor.viewBlog': 'View blog',
	'blog.editor.document': 'Document',
	'blog.editor.slugHelp': 'The URL is derived from the title and remains stable after the first save.',
	'blog.editor.showPreview': 'Show preview',
	'blog.editor.hidePreview': 'Hide preview',
	'blog.editor.previewEmpty': 'The Markdown preview appears here as you write.',
	'blog.editor.openPublished': 'Open published post',
	'blog.editor.saved': 'The post was saved.',
	'blog.comments.title': 'Discussion',
	'blog.comments.empty': 'No comments yet. Start the discussion.',
	'blog.comments.englishOnly': 'Blog comments are written in English. Code blocks and inline code may use other languages.',
	'blog.comments.yourComment': 'Your comment',
	'blog.comments.noImages': 'Markdown is supported. Images are not accepted in comments.',
	'blog.comments.post': 'Post comment',
	'blog.comments.saved': 'Your comment was published.',
	'blog.comments.failed': 'The comment could not be saved.',
	'blog.comments.locked': 'This discussion is read-only.',
	'blog.comments.toComment': 'to join the discussion.',
	'blog.comments.hide': 'Hide',
	'blog.comments.restore': 'Restore',
	'blog.comments.hideConfirm': 'Hide this comment from the public discussion?',
	'blog.comments.policy': 'Comments',
	'blog.comments.policyOpen': 'Open',
	'blog.comments.policyLocked': 'Read-only',
	'blog.comments.policyDisabled': 'Disabled',
	'blog.comments.moderation': 'Comment moderation',
    'docs.search': 'Search documentation',
    'docs.contents': 'In this article',
    'docs.popular': 'Popular guides',
    'docs.browse': 'Browse all documentation',
    'docs.installation': 'Installation',
    'docs.installation.detail': 'Install wavec and Vex, then verify the toolchain',
    'docs.firstProgram': 'First program',
    'docs.firstProgram.detail': 'Compile and run your first Wave source file',
    'docs.createProject': 'Create a project',
    'docs.compileRun': 'Compile and run',
    'docs.syntax': 'Basic syntax',
    'docs.types': 'Types',
    'docs.controlFlow': 'Control flow',
    'docs.functions': 'Functions',
    'docs.structures': 'Structures',
    'docs.generics': 'Generics',
    'docs.strings': 'Strings',
    'docs.files': 'Files',
    'docs.networking': 'Networking',
    'docs.systemApi': 'System APIs',
    'docs.tools': 'Tools',
    'docs.reference': 'Reference',
    'docs.navigation': 'Documentation navigation',
    'docs.filter': 'Filter documentation',
    'docs.overview': 'Overview',
    'docs.article': 'Article',
    'docs.breadcrumb': 'Breadcrumb',
    'docs.focus': 'Focus mode',
    'docs.exitFocus': 'Exit focus mode',
    'docs.sourceRevision': 'Source',
    'docs.previous': 'Previous',
    'docs.next': 'Next',
    'docs.noResults': 'No documents match this search.',
    'docs.notFound': 'Document not found',
    'docs.notFoundHelp': 'This document does not exist or is no longer published. Browse the documentation to find another page.',
    'docs.unavailable': 'Documentation could not be loaded. Please try again.',
    'docs.languageSelector': 'Documentation language',
    'docs.projectSelector': 'Documentation project',
    'docs.whaleLead': 'Whale guides and reference documentation.',
    'docs.stdlibLead': 'Module guides, API contracts, examples, and platform requirements.',
    'docs.learn': 'Learn Wave',
    'docs.practice': 'Practical projects',
    'docs.emptyProject': 'No documents have been published for this project yet.',
    'docs.englishFallback': 'This page has not been translated yet. Showing the English version.',
    'docs.englishFallbackCatalog': 'Documents without a translation are shown in English.',
    'community.title': 'Community',
    'community.personalTitle': 'LunaStev',
    'community.personalCategories': 'LunaStev categories',
    'community.personalAll': 'All',
    'community.lead': 'Official notices and public discussions.',
    'community.announcements': 'Notices',
    'community.discussions': 'Discussions',
    'community.tabs': 'Community sections',
    'community.empty.announcements': 'No notices have been published yet.',
    'community.empty.discussions': 'No discussions have been published yet.',
    'community.write': 'Write post',
    'community.all': 'All posts',
    'community.notice': 'Notice',
    'community.pinned': 'Pinned',
    'community.spaces': 'Spaces',
    'community.feed': 'Latest posts',
    'community.latest': 'Latest',
    'community.posts': 'posts',
    'community.replies': 'replies',
    'community.topics': 'Topics',
    'community.noTopics': 'No topics yet.',
    'community.empty': 'No posts yet.',
    'community.back': 'Community',
    'community.comments': 'Comments',
    'community.noComments': 'No comments yet.',
    'community.locked': 'Locked',
    'community.space.general': 'General',
    'community.space.founder-notes': 'Writing',
    'community.space.development-log': 'Development Log',
    'community.space.development': 'Development',
	'community.space.operating-systems': 'Operating Systems',
	'community.space.web': 'Web',
	'community.space.compiler': 'Compiler',
	'community.space.audio': 'Audio',
	'community.space.gui': 'GUI',
    'community.space.showcase': 'Showcase',
    'community.space.help': 'Help',
	'community.showcaseTitle': 'Showcase',
	'community.showcaseEyebrow': 'Made by the community',
	'community.showcaseHeading': 'Built with Wave',
	'community.showcaseLead': 'Explore tools, applications, experiments, and systems created by Wave community members.',
	'community.showcaseShare': 'Share a project',
	'community.search': 'Search posts',
	'community.createPost': 'Write a post',
	'community.category': 'Category',
	'community.postTitle': 'Title',
	'community.body': 'Body',
	'community.formatting': 'Formatting tools',
	'community.code': 'Code block',
	'community.link': 'Link',
	'community.quote': 'Quote',
	'community.imageUpload': 'Upload image',
	'community.imageUploading': 'Converting image to WebP…',
	'community.imageHelp': 'JPEG, PNG, or WebP up to 12 MiB. Images are resized to 1920 px and converted to WebP.',
	'community.imageUploadFailed': 'The image could not be uploaded.',
	'community.preview': 'Preview',
	'community.edit': 'Edit',
	'community.tags': 'Tags',
	'community.tagsPlaceholder': 'compiler, ffi, showcase',
	'community.englishOnly': 'Community posts and comments must be written in English. Code blocks and inline code may contain other languages.',
	'community.publish': 'Publish post',
	'community.reply': 'Reply',
	'community.addComment': 'Add a comment',
	'community.replying': 'Writing a reply',
	'community.postComment': 'Post comment',
	'community.signInToComment': 'Sign in to write a comment',
	'community.subscribe': 'Subscribe',
	'community.unsubscribe': 'Subscribed',
	'community.upvote': 'Upvote',
	'community.downvote': 'Downvote',
	'community.sort': 'Post order',
	'community.sort.latest': 'Latest',
	'community.sort.active': 'Active',
	'community.sort.top': 'Top',
	'community.activity': 'active',
	'community.popularTags': 'Popular tags',
	'community.categories': 'Community',
	'community.visiblePosts': 'visible posts',
	'community.noSearchResults': 'No posts match this search.',
	'community.actionFailed': 'The community request failed.',
    'mail.title': 'Mail',
    'mail.lead': 'Your Wave mailboxes and messages.',
	'mail.empty': 'There are no messages in this folder.',
    'mail.compose': 'Compose',
    'mail.inbox': 'Inbox',
    'mail.sent': 'Sent',
    'mail.drafts': 'Drafts',
    'mail.trash': 'Trash',
    'mail.folders': 'Folders',
    'mail.select': 'Select a message to read it.',
    'mail.signInRequired': 'Sign in to open your Wave mailbox.',
    'mail.signIn': 'Sign in to Wave Mail',
	'mail.starred': 'Starred',
	'mail.messages': 'Messages',
	'mail.reader': 'Message reader',
	'mail.selectMessage': 'Select a message to read it.',
    'mail.archive': 'Archive',
    'mail.delete': 'Delete',
    'mail.markUnread': 'Mark unread',
    'mail.refresh': 'Refresh',
    'mail.search': 'Search mail',
    'mail.emptyTitle': 'No messages in this folder',
    'mail.emptyDetail': 'New messages will appear here.',
    'mail.noSearchResults': 'No matching messages',
    'mail.changeSearch': 'Change the search terms and try again.',
    'mail.selectTitle': 'Select a message',
    'mail.selectDetail': 'Choose a message from the list to read it here.',
    'mail.newMessage': 'New message',
    'mail.to': 'To',
    'mail.toPlaceholder': 'recipient@example.com',
    'mail.delivery.queued': 'Queued',
    'mail.delivery.delivering': 'Sending',
    'mail.delivery.deferred': 'Retrying',
    'mail.delivery.delivered': 'Delivered',
    'mail.delivery.failed': 'Failed',
    'mail.subject': 'Subject',
    'mail.body': 'Message body',
    'mail.sendMessage': 'Send',
    'mail.sendFailed': 'The message could not be sent.',
	'mail.gmailWarning': 'Gmail may reject or delay Wave messages and may be unsuitable for security-sensitive recovery mail. When possible, use',
    'mail.actionFailed': 'The mailbox action could not be completed.',
	'mail.personal': 'Personal mail',
	'mail.personalDetail': 'Your private Wave mailbox',
	'mail.team': 'Team mail',
	'mail.teamDetail': 'Shared operational mail for owners and administrators',
	'mail.newTeamMessage': 'New team message',
	'mail.reply': 'Reply',
	'mail.hubModes': 'Mail areas',
	'mail.lists.title': 'Mailing lists',
	'mail.lists.modeDetail': 'Internal project discussions delivered through Wave Mail',
	'mail.lists.internalOnly': 'Available only to active Wave accounts.',
	'mail.lists.directory': 'Mailing list directory',
	'mail.lists.subscribed': 'Subscribed',
	'mail.lists.threads': 'Threads',
	'mail.lists.join': 'Join',
	'mail.lists.leave': 'Leave',
	'mail.lists.newThread': 'New thread',
	'mail.lists.patchReview': 'Patch review queue',
	'mail.lists.patchSubjectPlaceholder': '[PATCH] compiler: describe the change',
	'mail.lists.search': 'Search this list',
	'mail.lists.messages': 'messages',
	'mail.lists.emptyTitle': 'No discussions yet',
	'mail.lists.emptyDetail': 'Start the first thread for this mailing list.',
	'mail.lists.joinToPost': 'Join this list to start a discussion or reply.',
	'mail.lists.threadReader': 'Mailing list thread',
	'mail.lists.subscriptionFailed': 'The subscription could not be changed.',
	'mail.lists.postFailed': 'The thread could not be published.',
	'mail.lists.replyFailed': 'The reply could not be sent.',
	'mail.lists.publish': 'Publish thread',
	'mail.lists.reply': 'Reply to this thread',
	'mail.lists.sendReply': 'Send reply',
	'mail.lists.selectTitle': 'Select a thread',
	'mail.lists.selectDetail': 'Choose a discussion from the list to read and reply here.',
    'questions.title': 'Questions',
    'questions.lead': 'Ask about Wave and find accepted answers.',
    'questions.empty': 'No questions have been published yet.',
    'questions.search': 'Search questions',
    'questions.ask': 'Ask a question',
    'questions.newest': 'Newest',
    'questions.unanswered': 'Unanswered',
    'questions.active': 'Active',
    'questions.filters': 'Question filters',
    'questions.tags': 'Popular tags',
    'questions.guide': 'Before asking, check the documentation and include your Wave version.',
    'questions.votes': 'votes',
    'questions.answers': 'answers',
    'questions.views': 'views',
    'questions.status.open': 'Open',
    'questions.status.answered': 'Answered',
    'questions.status.resolved': 'Resolved',
    'questions.status.closed': 'Closed',
    'questions.questionTitle': 'Title',
    'questions.details': 'Question details',
    'questions.tagsLabel': 'Tags',
    'questions.tagsPlaceholder': 'compiler, generics, linux',
    'questions.waveVersion': 'Wave version',
    'questions.platform': 'Platform',
    'questions.publish': 'Publish question',
    'questions.vote': 'Vote',
    'questions.accept': 'Accept this answer',
    'questions.accepted': 'Accepted answer',
    'questions.yourAnswer': 'Your answer',
    'questions.postAnswer': 'Post answer',
    'questions.toAnswer': 'to post an answer.',
    'questions.createFailed': 'The question could not be published.',
    'questions.answerFailed': 'The answer could not be posted.',
    'questions.voteFailed': 'The vote could not be saved.',
    'questions.acceptFailed': 'The accepted answer could not be changed.',
    'source.title': 'Source',
    'source.lead': 'Browse the official compiler and standard library source.',
    'source.repositories': 'Repositories',
	'source.sections': 'Source sections',
	'patches.title': 'Patches',
	'patches.lead': 'Internal review queue for Git patches submitted by Wave accounts.',
	'patches.submit': 'Submit a patch by email',
	'patches.submitHelp': 'Submit valid Git patch messages from your Wave account. Only project members can view the review queue.',
	'patches.search': 'Search patches, authors, and changed files',
	'patches.files': 'files',
	'patches.empty': 'No public patches match this search.',
	'patches.notFound': 'The patch could not be found.',
	'patches.back': 'All patches',
	'patches.changedFiles': 'Changed files',
	'patches.review': 'Maintainer review',
	'patches.reviewHelp': 'Official review controls are available only to the founder and designated Source Maintainers. Patches are never applied by this server.',
	'patches.reviewStatus': 'Review status',
	'patches.targetRepository': 'Target repository',
	'patches.targetRepositoryPlaceholder': 'wavefnd/Wave',
	'patches.assignee': 'Assigned maintainer',
	'patches.checksum': 'Original SHA-256',
	'patches.downloadMbox': 'Download mbox',
	'patches.downloadSeries': 'Download series mbox',
	'patches.saveReview': 'Save review',
	'patches.reviewSaved': 'Patch review saved.',
	'patches.status.received': 'Received',
	'patches.status.reviewing': 'Reviewing',
	'patches.status.accepted': 'Accepted',
	'patches.status.rejected': 'Rejected',
	'patches.status.applied': 'Applied',
	'patches.comments': 'comments',
	'patches.generalComments': 'Review discussion',
	'patches.noComments': 'No review comments yet.',
	'patches.addGeneralComment': 'Add a general review comment',
	'patches.addComment': 'Add comment',
	'patches.commentSaved': 'Review comment added.',
	'patches.commentResolved': 'Review comment resolved.',
	'patches.commentReopened': 'Review comment reopened.',
	'patches.resolved': 'Resolved',
	'patches.resolve': 'Resolve',
	'patches.reopen': 'Reopen',
	'patches.diff': 'Patch diff with inline review comments',
	'patches.commentOnLine': 'Comment on line {line}',
	'patches.commentingOn': 'Commenting on {path}, patch line {line}',
	'rfc.title': 'Request for Comments',
	'rfc.lead': 'Public design proposals for significant changes to the Wave language and platform.',
	'rfc.all': 'All RFCs',
	'rfc.back': 'Back to RFCs',
	'rfc.new': 'New RFC',
	'rfc.create': 'Create a proposal',
	'rfc.edit': 'Edit proposal',
	'rfc.editorHelp': 'RFCs record motivation, a concrete design, compatibility impact, security considerations, and alternatives. Drafts are public.',
	'rfc.proposalTitle': 'Proposal title',
	'rfc.content': 'Proposal document (Markdown)',
	'rfc.search': 'Search RFCs, authors, and proposals',
	'rfc.filterStatus': 'Filter by status',
	'rfc.allStatuses': 'All statuses',
	'rfc.empty': 'No RFCs match these filters.',
	'rfc.by': 'Proposed by {author}',
	'rfc.officialStatus': 'Official status',
	'rfc.updateStatus': 'Record decision',
	'rfc.confirmStatus': 'Change this RFC’s official status to “{status}”? This decision will be recorded in the audit log.',
	'rfc.statusSaved': 'The official RFC status was updated.',
	'rfc.discussion': 'Public discussion',
	'rfc.discussionHelp': 'Discuss the design and its consequences. Keep implementation details connected to the proposal.',
	'rfc.noComments': 'No discussion comments yet.',
	'rfc.yourComment': 'Your comment',
	'rfc.addComment': 'Add comment',
	'rfc.toComment': 'to join the RFC discussion.',
	'rfc.commentSaved': 'Your RFC comment was added.',
	'rfc.commentFailed': 'The RFC comment could not be saved.',
	'rfc.saveFailed': 'The RFC could not be saved.',
	'rfc.status.draft': 'Draft',
	'rfc.status.discussion': 'Discussion',
	'rfc.status.accepted': 'Accepted',
	'rfc.status.rejected': 'Rejected',
	'rfc.status.implementing': 'Implementing',
	'rfc.status.completed': 'Completed',
	'rfc.status.withdrawn': 'Withdrawn',
    'source.searchRepositories': 'Search repositories',
    'source.code': 'Code',
    'source.commits': 'Commits',
    'source.tags': 'Tags',
    'source.name': 'Name',
    'source.updated': 'Updated',
    'source.projects': 'Repositories',
    'source.compilerDescription': 'Wave language compiler and standard library.',
    'source.vexDescription': 'Package manager and build tool for Wave projects.',
    'source.whaleDescription': 'Low-level assembler, object, linker, and IR toolchain.',
    'source.languages': 'Detected languages',
    'source.detected': 'Detected from repository files',
    'source.allLanguages': 'All',
    'source.filterLanguage': 'Filter by language',
    'source.primaryLanguage': 'Primary',
    'source.openProject': 'Open repository',
    'source.public': 'Public',
    'source.mirror': 'Mirror',
    'source.about': 'About',
    'source.lastCommit': 'Last commit',
    'source.lastUpdated': 'Last updated',
    'source.branches': 'Branches',
    'source.branch': 'Branch',
    'source.tag': 'Tag',
    'source.commit': 'Commit',
    'source.reference': 'Branch or tag',
    'source.history': 'History',
    'source.openFile': 'Open file',
    'source.changedFiles': 'changed files',
    'source.diffTruncated': 'This diff is too large and has been truncated.',
    'source.path': 'Repository path',
    'source.binaryFile': 'Binary files cannot be displayed.',
    'admin.title': 'Administration',
	'admin.blog': 'Blog',
	'admin.webhooks': 'Webhooks',
	'admin.webhookConfiguration': 'Webhook configuration',
	'admin.webhookHelp': 'Send new community, LunaStev, release, and patch events to Discord or a generic HTTPS endpoint.',
	'admin.newWebhook': 'New webhook',
	'admin.webhookName': 'Name',
	'admin.webhookKind': 'Format',
	'admin.webhookUrl': 'Destination URL',
	'admin.webhookUrlRetained': 'Leave blank to keep the encrypted destination URL.',
	'admin.webhookEvents': 'Events',
	'admin.rotateWebhookSecret': 'Rotate signing secret',
	'admin.webhookSecretOnce': 'Copy this signing secret now. It will not be shown again.',
	'admin.webhookEndpoints': 'Configured endpoints',
	'admin.webhookDeliveries': 'Recent deliveries',
	'admin.testWebhook': 'Test',
	'admin.confirmDeleteWebhook': 'Delete this webhook? Delivery history and audit records remain.',
	'admin.noWebhooks': 'No webhook endpoints have been configured.',
	'admin.noWebhookDeliveries': 'No webhook deliveries have been recorded.',
	'admin.blogHelp': 'Write releases, official articles, and ordered version roadmaps in Markdown.',
	'admin.blogPosts': 'Blog posts',
	'admin.newPost': 'New post',
	'admin.editPost': 'Edit post',
	'admin.blogSlug': 'URL slug',
	'admin.blogTitle': 'Title',
	'admin.blogCategory': 'Category',
	'admin.roadmapStatus': 'Roadmap status',
	'admin.selectRoadmapStatus': 'Select a status',
	'admin.roadmapOrder': 'Display order',
	'admin.roadmapOrderHelp': 'Lower numbers appear first. Target dates do not control the order.',
	'admin.targetReleaseDate': 'Target release date',
	'admin.blogSummary': 'Summary',
	'admin.blogContent': 'Markdown content',
	'admin.blogStatus': 'Publication status',
	'admin.blogPreview': 'Preview',
	'admin.draft': 'Draft',
	'admin.published': 'Published',
	'admin.blogSaved': 'The blog post was saved.',
	'admin.noBlogPosts': 'No blog posts have been created.',
    'admin.lead': 'Runtime and service state from the live Wave Platform.',
    'admin.modules': 'Modules',
    'admin.overview': 'Overview',
    'admin.platform': 'Platform',
    'admin.platformOverview': 'Platform overview',
    'admin.accounts': 'Accounts',
    'admin.messagesToday': 'Messages today',
    'admin.gitMirrors': 'Git mirrors',
    'admin.backToPlatform': 'Wave Platform',
    'admin.module': 'Module',
    'admin.status': 'Status',
    'admin.availability': 'Availability',
    'admin.enabled': 'Enabled',
    'admin.disabled': 'Disabled',
    'admin.management': 'Management',
    'admin.accountManagement': 'Account management',
    'admin.accountManagementHelp': 'Search accounts, review access, and manage account state.',
    'admin.account': 'Account',
    'admin.searchAccounts': 'Search accounts',
    'admin.role': 'Role',
    'admin.owner': 'Owner',
    'admin.administrator': 'Administrator',
	'admin.sourceMaintainer': 'Source Maintainer',
	'admin.rfcMaintainer': 'RFC Maintainer',
    'admin.member': 'Member',
    'admin.actions': 'Actions',
    'admin.suspended': 'Suspended',
    'admin.suspend': 'Suspend',
    'admin.restore': 'Restore',
    'admin.makeAdmin': 'Make administrator',
    'admin.removeAdmin': 'Remove administrator',
	'admin.makeSourceMaintainer': 'Make Source Maintainer',
	'admin.removeSourceMaintainer': 'Remove Source Maintainer',
	'admin.makeRFCMaintainer': 'Make RFC Maintainer',
	'admin.removeRFCMaintainer': 'Remove RFC Maintainer',
    'admin.recoveryVerified': 'Recovery verified',
    'admin.recoveryUnverified': 'Recovery not verified',
    'admin.noAccounts': 'No accounts match this search.',
    'admin.mailQueue': 'Mail delivery queue',
    'admin.mailAttention': 'Mail requiring attention',
    'admin.recentDeliveries': '50 most recent deliveries',
    'admin.queued': 'Queued',
    'admin.delivering': 'Delivering',
    'admin.deferred': 'Deferred',
    'admin.failed': 'Failed',
    'admin.delivered': 'Delivered',
    'admin.recipient': 'Recipient',
    'admin.destination': 'Destination',
    'admin.attempts': 'Attempts',
    'admin.updated': 'Updated',
    'admin.noDeliveries': 'No mail deliveries have been recorded.',
    'admin.syncEvery': 'Automatic synchronization every',
    'admin.repository': 'Repository',
    'admin.branch': 'Branch',
    'admin.head': 'HEAD',
    'admin.lastFetched': 'Last fetched',
    'admin.security': 'Authentication and security',
    'admin.totpEnrollment': 'TOTP enrollment',
    'admin.verifiedRecovery': 'Verified recovery email',
    'admin.registration': 'Registration',
    'admin.open': 'Open',
    'admin.closed': 'Closed',
    'admin.system': 'System and storage',
    'admin.databaseHealth': 'Database health',
    'admin.lsmStorage': 'Badger LSM',
    'admin.valueLog': 'Badger value log',
    'admin.totalStorage': 'Total data files',
    'admin.storageUsed': 'Storage used',
    'admin.ready': 'Ready',
    'admin.auditLog': 'Audit log',
    'admin.recentEvents': '100 most recent events',
    'admin.time': 'Time',
    'admin.actor': 'Actor',
    'admin.action': 'Action',
    'admin.resource': 'Resource',
    'admin.result': 'Result',
    'admin.noAuditEvents': 'No audit events have been recorded.',
    'admin.refresh': 'Refresh',
    'admin.confirmAction': 'Confirm management action',
    'admin.confirmSuspend': 'Suspend this account and revoke all of its active sessions?',
    'admin.confirmRestore': 'Restore access for this account?',
    'admin.confirmMakeAdmin': 'Grant platform administrator access to this account?',
    'admin.confirmRemoveAdmin': 'Remove platform administrator access and revoke active sessions?',
	'admin.confirmMakeSourceMaintainer': 'Allow this account to officially review and download Git patches? This does not grant administrator access.',
	'admin.confirmRemoveSourceMaintainer': 'Remove this account’s Git patch review and download access?',
	'admin.confirmMakeRFCMaintainer': 'Allow this account to record official RFC decisions? This does not grant administrator access.',
	'admin.confirmRemoveRFCMaintainer': 'Remove this account’s RFC decision access?',
    'admin.confirm': 'Confirm',
    'admin.actionFailed': 'The management action failed.',
	'admin.overviewLead': 'Operational summary. Open a management section for detailed data and controls.',
	'admin.managementMailbox': 'Management mailbox',
	'admin.managementMailboxEmpty': 'No messages have arrived in the management mailbox.',
	'admin.lunaStevTimeZone': 'LunaStev display time zone',
	'admin.lunaStevTimeZoneHelp': 'Applies only to LunaStev. Community time display is unchanged.',
	'user.directory': 'User directory',
	'user.people': 'People',
	'user.allUsers': 'All users',
	'user.profile': 'Profile',
	'user.joined': 'Joined',
	'user.editProfile': 'Edit profile',
	'user.profileSettings': 'Profile settings',
	'user.bio': 'Introduction',
	'user.timeZone': 'Time zone',
	'user.waveAddress': 'Wave mail identity',
	'user.localPart': 'Mail address',
	'user.addressIdentityHelp': 'Your Wave mail address is your public unique identifier. Changing it requires your authenticator code.',
	'user.changeAddress': 'Change mail address',
	'user.profileSaved': 'Profile settings were saved.',
	'user.addressSaved': 'Wave mail address was changed.',
	'user.updateFailed': 'The profile could not be updated.',
	'user.activity': 'Activity',
	'user.activity.all': 'All',
	'user.activity.community-post': 'Posts',
	'user.activity.community-comment': 'Comments',
	'user.activity.question': 'Questions',
	'user.activity.answer': 'Answers',
	'user.noActivity': 'No public activity yet.',
    'common.loadError': 'Unable to load data.',
    'common.loading': 'Loading…',
	'common.enabled': 'Enabled',
	'common.disabled': 'Disabled',
    'common.retry': 'Retry',
    'common.more': 'More',
	'common.cancel': 'Cancel',
	'common.save': 'Save',
	'common.edit': 'Edit',
	'common.delete': 'Delete',
	'common.close': 'Close',
	'mail.from': 'From',
    'common.skipContent': 'Skip to main content',
    'notFound.title': 'Page not found',
    'notFound.lead': 'The requested page does not exist.',
    'notFound.home': 'Return to the home page',
    'login.title': 'Sign in',
    'register.title': 'Create a Wave account',
  },
  ko: {
    'playground.title': "플레이그라운드",
    'playground.intro': "브라우저에서 Wave 코드를 작성하고 실행하세요. 아래 예제를 수정하며 시작할 수 있습니다.",
    'playground.stdin': "표준 입력",
    'playground.stdinHelp': "실행 전에 입력할 내용을 적으세요. 입력을 모두 읽으면 EOF를 반환합니다.",
    'playground.run': "실행",
    'playground.stop': "중지",
    'playground.open': "플레이그라운드 열기",
    'playground.output': "실행 결과",
    'playground.terminal': "터미널",
    'playground.error': "오류",
    'playground.idle': "준비됨",
    'playground.compile': "컴파일 중…",
    'playground.running': "실행 중…",
    'playground.done': "완료",
    'playground.stopped': "중지됨",
    'playground.exit': "종료 코드",
    'playground.noOutput': "출력 없이 실행을 마쳤습니다.",
    'playground.limits': "실행 3초 · 메모리 64 MiB · 코드 32 KiB. 파일과 네트워크 접근은 지원하지 않습니다.",
    'playground.compileTimeout': "컴파일 시간이 초과되었습니다. 다시 시도해 주세요.",
    'playground.runTimeout': "실행 시간이 3초를 초과해 중지했습니다.",
    'playground.failed': "프로그램을 실행하지 못했습니다.",
    'playground.reset': "초기화",
    'playground.options': "입력 및 실행 정보",
    'home.languageTitle': 'Wave 프로그래밍 언어',
    'home.languageIntro': 'Wave는 C를 현대적으로 발전시킨 범용 언어입니다. 네이티브 컴파일, 명시적 타입, 직접적인 제어를 바탕으로 애플리케이션, 명령줄 도구, 시스템 소프트웨어를 만듭니다.',
    'home.developmentStatus': 'Wave는 현재 pre-beta 단계에서 활발히 개발 중입니다. 릴리즈 사이에 문법과 툴체인 규약이 바뀔 수 있습니다.',
    'home.exampleTitle': '첫 Wave 프로그램',
    'home.exampleSave': '다음 전체 프로그램을 main.wave로 저장하세요:',
    'home.exampleRun': 'Wave를 설치한 뒤 main.wave가 있는 디렉터리에서 다음 명령을 실행하세요:',
    'home.exampleOutput': '실행 결과:',
    'community.welcomeTitle': 'Wave 커뮤니티에 오신 것을 환영합니다',
    'community.welcomeBody': '자기소개를 하거나 만들고 있는 프로젝트를 공유하고 Wave에 대해 이야기해 보세요. 도움이 필요하면 운영체제, Wave 버전, 작은 재현 예제와 오류 메시지 전문을 함께 알려 주세요.',
    'community.askQuestion': '질문하기',
    'community.contribute': 'Wave에 기여하기',
    'nav.toolchains': '툴체인',
    'brand.subtitle': '플랫폼',
    'nav.docs': '문서',
	'nav.blog': '블로그',
    'nav.home': '홈',
    'nav.source': '소스',
    'nav.community': '커뮤니티',
    'nav.personal': 'LunaStev',
    'nav.admin': '관리',
    'nav.questions': '질문',
	'nav.rfc': 'RFC',
	'nav.explore': '더 보기',
	'nav.discuss': '소통',
	'nav.workspace': '내 공간',
	'nav.manage': '관리',
	'editor.toolbar': 'WaveEditor 서식',
	'editor.status': 'WaveEditor 문서 상태',
	'editor.bold': '굵게',
	'editor.italic': '기울임',
	'editor.inlineCode': '인라인 코드',
	'editor.heading': '제목',
	'editor.quote': '인용',
	'editor.list': '목록',
	'editor.link': '링크',
	'editor.characters': '자',
	'editor.words': '단어',
	'editor.lines': '줄',
	'editor.waveCore': 'Wave 코어',
	'editor.compatibility': 'Wave 호환 엔진',
    'nav.mail': '메일',
    'nav.github': 'GitHub',
    'nav.discord': 'Discord 커뮤니티',
	'notifications.title': '알림',
	'notifications.markAllRead': '모두 읽음',
	'notifications.loading': '알림을 불러오는 중…',
	'notifications.failed': '알림을 불러오지 못했습니다.',
	'notifications.empty': '아직 알림이 없습니다.',
	'notifications.unread': '읽지 않음',
	'notifications.kind.blog.comment': '새 블로그 댓글',
	'notifications.kind.community.reply': '새 토론 답글',
	'notifications.kind.question.answer': '새 답변',
	'notifications.kind.question.accepted': '답변 채택',
	'notifications.kind.rfc.comment': '새 RFC 댓글',
	'notifications.kind.rfc.status': 'RFC 상태 변경',
    'nav.menu': '메뉴 열기',
    'nav.menuButton': '메뉴',
    'nav.primary': '주 메뉴',
    'nav.language': '언어',
    'theme.label': '테마',
    'theme.system': '시스템 테마',
    'theme.light': '밝은 테마',
    'theme.black': '블랙 테마',
    'theme.switchLight': '밝은 테마로 전환',
    'theme.switchBlack': '블랙 테마로 전환',
    'nav.search': 'Wave Platform 검색',
    'nav.searchButton': '검색',
    'search.scope.portal': 'Wave Platform 검색',
    'search.scope.community': '커뮤니티 검색',
    'search.scope.docs': '문서 검색',
    'search.scope.questions': '질문 검색',
    'search.scope.mail': '메일 검색',
    'search.scope.source': '저장소 검색',
    'search.scope.account': 'Wave Platform 검색',
    'auth.signIn': '로그인',
    'auth.signUp': '회원가입',
	'auth.signOut': '로그아웃',
    'auth.account': '계정',
    'auth.username': '사용자명 또는 이메일',
    'auth.displayName': '표시 이름',
	'auth.waveAddress': 'Wave 메일 주소',
	'auth.waveAddressHelp': '표시 이름에서 고유한 Wave 주소를 자동으로 만듭니다.',
	'auth.duplicateNameAddressHelp': '자동 생성 주소가 이미 사용 중입니다. 동명 계정을 구분할 다른 고유 주소를 선택하세요.',
	'auth.addressChoiceRequired': '동명 계정을 구분할 다른 Wave 메일 주소를 선택하세요.',
    'auth.email': '이메일',
    'auth.notAvailable': '계정 가입과 로그인은 아직 사용할 수 없습니다.',
    'auth.submitSignIn': '로그인',
    'auth.submitSignUp': '계정 만들기',
	'auth.failed': '계정 요청을 처리하지 못했습니다.',
	'auth.registrationClosed': '현재 회원가입을 받지 않습니다.',
	'auth.authenticatorCode': '인증 앱의 6자리 코드',
	'auth.recoveryEmail': '복구 이메일',
	'auth.recoveryEmailHelp': '인증 앱을 잃어버렸을 때만 사용하며 확인 메일을 보냅니다.',
	'auth.setupAuthenticator': '인증 앱 연결하기',
	'auth.connectAuthenticator': '인증 앱 연결',
	'auth.totpUnavailable': '이 서버에 인증 앱 로그인이 설정되지 않았습니다.',
	'auth.scanAuthenticator': '인증 앱으로 QR 코드를 스캔한 뒤 6자리 코드를 입력하세요.',
	'auth.scanNewAuthenticator': '새 QR 코드를 스캔한 뒤 6자리 코드를 입력하세요.',
	'auth.qrCode': '인증 앱 설정 QR 코드',
	'auth.manualKey': '설정 키를 직접 입력하기',
	'auth.finishAccount': '계정 만들기',
	'auth.lostAuthenticator': '인증 앱을 잃어버렸나요?',
	'auth.recoveryTitle': '계정 복구',
	'auth.recoveryHelp': '확인된 복구 이메일로 일회용 재설정 링크를 보냅니다.',
	'auth.sendRecovery': '복구 링크 보내기',
	'auth.recoverySent': '확인된 복구 주소가 있는 계정이라면 재설정 링크를 보냈습니다.',
	'auth.checkSpam': '메일이 보이지 않으면 스팸메일함을 확인해 주세요.',
	'auth.backToSignIn': '로그인으로 돌아가기',
	'auth.recoveryExpired': '복구 링크가 잘못되었거나 만료되었습니다.',
	'auth.resetAuthenticator': '인증 앱 재설정',
	'auth.resetComplete': '인증 앱을 교체했습니다. 새 코드로 로그인하세요.',
	'auth.verifyRecovery': '복구 이메일 확인',
	'auth.recoveryVerified': '복구 이메일을 확인했습니다.',
	'auth.verificationExpired': '확인 링크가 잘못되었거나 만료되었습니다.',
	'auth.securitySettings': '보안 설정',
	'auth.webhookHelp': 'Wave 계정을 Discord 또는 외부 Wave 연동 서비스에 연결합니다. HTTPS 엔드포인트를 최대 10개까지 만들 수 있습니다.',
	'auth.webhookSaved': '웹후크를 저장했습니다.',
	'auth.webhookTestSent': '웹후크 시험 전송을 완료했습니다.',
	'auth.settings': '설정',
	'auth.authenticator': '인증 앱',
	'auth.authenticatorStatus': 'TOTP 로그인:',
	'auth.currentCode': '현재 인증 앱 코드',
	'auth.newCode': '새 인증 앱 코드',
	'auth.replaceAuthenticator': '인증 앱 교체',
	'auth.confirmReplacement': '교체 확인',
	'auth.newRecoveryEmail': '새 복구 이메일',
	'auth.sendVerification': '확인 메일 보내기',
	'auth.verificationSent': '새 주소로 확인 링크를 보냈습니다.',
	'auth.verified': '확인됨',
	'auth.unverified': '확인되지 않음',
    'footer.official': 'Wave Foundation 공식 사이트',
    'footer.admin': '관리',
    'home.title': 'Wave Platform',
    'home.intro': 'Wave 문서, 소스, 릴리즈, 커뮤니티, 질문과 메일을 제공하는 공식 포털입니다.',
    'home.services.title': '서비스',
    'home.about.title': 'Wave 소개',
    'home.about.body': 'Wave는 네이티브 프로그램, 시스템 도구와 하드웨어에 가까운 소프트웨어를 위해 설계되었습니다. 명시적인 타입과 예측 가능한 컴파일을 중요하게 생각합니다.',
    'home.start.title': '시작하기',
    'home.start.install': '지원되는 Unix 계열 환경에서 최신 컴파일러를 설치합니다:',
    'home.start.example': 'hello.wave 파일을 만듭니다:',
    'home.start.docs': '설치 및 언어 문서 읽기',
    'home.news.title': '소식',
    'home.news.archive': 'Wave v0.1.9-pre-beta 릴리즈 노트',
    'home.download.title': '다운로드',
    'home.download.body': '공식 배포 파일과 릴리즈 노트는 GitHub에서 제공합니다.',
    'home.project.title': '프로젝트 링크',
    'home.account.title': 'Wave 계정',
    'home.release.title': '릴리즈 모음',
    'home.releaseNotes': '릴리즈 노트 읽기',
    'home.stats.title': '플랫폼 활동',
    'home.stats.accounts': 'Wave 계정',
    'home.stats.messages': '오늘 처리된 메일',
    'home.stats.mirrors': '활성 소스 미러',
    'home.start.eyebrow': 'Wave 툴체인',
    'home.releaseLabel': '릴리즈',
    'home.githubReleases': 'GitHub 릴리즈',
    'home.compiler': 'Wave 컴파일러',
    'home.releaseArchive': '릴리즈 모음',
    'home.communityLatest': '커뮤니티 최신 토론',
    'home.docsStart': '문서에서 시작하기',
    'home.installWave': 'Wave 툴체인 설치',
    'home.installPlatform': '설치 플랫폼',
    'home.latestReleases': '최신 릴리즈',
    'home.copyInstall': '설치 명령 복사',
    'home.recentSource': '최근 변경된 소스',
    'home.sourceEmpty': '아직 표시할 소스 미러 데이터가 없습니다.',
    'home.personalEmpty': '아직 공개된 글이나 개발 일지가 없습니다.',
    'home.unansweredQuestions': '답변을 기다리는 질문',
	'home.sponsors': '후원자',
	'home.sponsorProject': 'Wave 후원하기',
	'home.noSponsors': 'Wave의 첫 후원자가 되어주세요.',
	'home.oneTimeSupporters': '일회성 후원자',
    'search.title': '검색',
    'search.lead': '문서, 커뮤니티 글, 질문, 메일과 소스를 검색합니다.',
    'search.empty': '검색 기능은 아직 사용할 수 없습니다.',
    'docs.title': '문서',
	'blog.official': '공식 발행',
	'blog.title': 'Wave 블로그',
	'blog.lead': 'Wave 프로젝트의 릴리즈 발표, 개발 결정과 공식 소식을 전합니다.',
	'blog.empty': '아직 발행된 블로그 글이 없습니다.',
	'blog.back': '블로그로 돌아가기',
	'blog.categories': '블로그 분류',
	'blog.category.all': '전체',
	'blog.category.article': '글',
	'blog.category.release': '릴리즈',
	'blog.category.roadmap': '로드맵',
	'blog.release.title': 'Wave 릴리즈',
	'blog.release.lead': '출시된 Wave 버전과 출시일, 버전별 주요 변경 사항을 확인합니다.',
	'blog.release.archive': 'Wave 릴리즈 모음',
	'blog.release.version': '버전',
	'blog.release.date': '출시일',
	'blog.release.back': '릴리즈로 돌아가기',
	'blog.release.empty': '아직 출시된 Wave 버전이 없습니다.',
	'blog.release.notFound': '요청한 Wave 릴리즈를 찾을 수 없습니다.',
	'blog.roadmap.planned': '계획됨',
	'blog.roadmap.inProgress': '진행 중',
	'blog.roadmap.released': '출시됨',
	'blog.roadmap.target': '출시 예정일',
	'blog.roadmap.empty': '아직 공개된 로드맵 항목이 없습니다.',
	'blog.editor.title': '블로그 편집기',
	'blog.editor.lead': 'WaveEditor로 Wave 공식 글, 릴리즈와 로드맵을 작성하고 미리 봅니다.',
	'blog.editor.viewBlog': '블로그 보기',
	'blog.editor.document': '문서',
	'blog.editor.slugHelp': 'URL은 제목에서 자동 생성되며 처음 저장한 뒤에는 유지됩니다.',
	'blog.editor.showPreview': '미리보기 표시',
	'blog.editor.hidePreview': '미리보기 숨기기',
	'blog.editor.previewEmpty': '작성한 Markdown 미리보기가 여기에 표시됩니다.',
	'blog.editor.openPublished': '발행된 글 열기',
	'blog.editor.saved': '글을 저장했습니다.',
	'blog.comments.title': '댓글',
	'blog.comments.empty': '아직 댓글이 없습니다. 첫 의견을 남겨보세요.',
	'blog.comments.englishOnly': '블로그 댓글은 영어로 작성합니다. 코드 블록과 인라인 코드는 다른 언어를 사용할 수 있습니다.',
	'blog.comments.yourComment': '댓글 내용',
	'blog.comments.noImages': 'Markdown을 지원하며 댓글에는 이미지를 첨부할 수 없습니다.',
	'blog.comments.post': '댓글 게시',
	'blog.comments.saved': '댓글을 게시했습니다.',
	'blog.comments.failed': '댓글을 저장하지 못했습니다.',
	'blog.comments.locked': '이 댓글 공간은 읽기 전용입니다.',
	'blog.comments.toComment': '후 댓글을 작성할 수 있습니다.',
	'blog.comments.hide': '숨기기',
	'blog.comments.restore': '복구',
	'blog.comments.hideConfirm': '이 댓글을 공개 목록에서 숨길까요?',
	'blog.comments.policy': '댓글',
	'blog.comments.policyOpen': '작성 허용',
	'blog.comments.policyLocked': '읽기 전용',
	'blog.comments.policyDisabled': '사용 안 함',
	'blog.comments.moderation': '댓글 관리',
    'docs.lead': '언어 안내서와 참고 문서입니다.',
    'docs.gettingStarted': '시작하기',
    'docs.gettingStarted.detail': '설치, 첫 프로그램과 wavec 기본 사용법',
    'docs.language': 'Wave 배우기',
    'docs.language.detail': '타입, 제어 흐름, 구조체, 제네릭과 FFI',
    'docs.standardLibrary': '표준 라이브러리',
    'docs.standardLibrary.detail': '문자열, 파일, 메모리, 네트워크와 시스템 API',
    'docs.compiler': '컴파일러',
    'docs.compiler.detail': '빌드 옵션, 타깃, 진단과 ABI',
    'docs.ecosystem': '생태계',
    'docs.ecosystem.detail': 'Vex, Whale, WSON과 관련 프로젝트',
    'docs.search': '문서 검색',
    'docs.contents': '이 문서의 내용',
    'docs.popular': '많이 찾는 문서',
    'docs.browse': '전체 문서 둘러보기',
    'docs.installation': '설치',
    'docs.installation.detail': 'wavec와 Vex를 설치하고 툴체인을 확인합니다',
    'docs.firstProgram': '첫 프로그램',
    'docs.firstProgram.detail': '첫 Wave 소스 파일을 컴파일하고 실행합니다',
    'docs.createProject': '프로젝트 만들기',
    'docs.compileRun': '컴파일과 실행',
    'docs.syntax': '기본 문법',
    'docs.types': '타입',
    'docs.controlFlow': '제어 흐름',
    'docs.functions': '함수',
    'docs.structures': '구조체',
    'docs.generics': '제네릭',
    'docs.strings': '문자열',
    'docs.files': '파일',
    'docs.networking': '네트워크',
    'docs.systemApi': '시스템 API',
    'docs.tools': '도구',
    'docs.reference': '참조',
    'docs.navigation': '문서 탐색',
    'docs.filter': '문서 목록 필터',
    'docs.overview': '전체 문서',
    'docs.article': '문서',
    'docs.breadcrumb': '현재 위치',
    'docs.focus': '집중 모드',
    'docs.exitFocus': '집중 모드 종료',
    'docs.sourceRevision': '소스',
    'docs.previous': '이전 문서',
    'docs.next': '다음 문서',
    'docs.noResults': '검색 조건과 일치하는 문서가 없습니다.',
    'docs.notFound': '문서를 찾을 수 없습니다',
    'docs.notFoundHelp': '존재하지 않거나 더 이상 게시되지 않은 문서입니다. 문서 목록에서 다른 페이지를 찾아보세요.',
    'docs.unavailable': '문서를 불러오지 못했습니다. 다시 시도해 주세요.',
    'docs.languageSelector': '문서 언어',
    'docs.projectSelector': '문서 프로젝트',
    'docs.whaleLead': 'Whale 안내와 참조 문서입니다.',
    'docs.stdlibLead': '모듈별 사용법, API 규칙, 실행 예제와 플랫폼 요구 사항을 확인합니다.',
    'docs.learn': 'Wave 배우기',
    'docs.practice': '실전 프로그램',
    'docs.emptyProject': '이 프로젝트에 아직 게시된 문서가 없습니다.',
    'docs.englishFallback': '아직 번역되지 않은 문서라 영어 번역본을 표시합니다.',
    'docs.englishFallbackCatalog': '아직 번역되지 않은 문서는 영어로 표시합니다.',
    'community.title': '커뮤니티',
    'community.personalTitle': 'LunaStev',
    'community.personalCategories': 'LunaStev 분류',
    'community.personalAll': '전체',
    'community.lead': '공식 공지와 공개 토론입니다.',
    'community.announcements': '공지',
    'community.discussions': '토론',
    'community.tabs': '커뮤니티 영역',
    'community.empty.announcements': '아직 등록된 공지가 없습니다.',
    'community.empty.discussions': '아직 공개된 토론이 없습니다.',
    'community.write': '글 작성',
    'community.all': '전체 글',
    'community.notice': '공지',
    'community.pinned': '고정',
    'community.spaces': '공간',
    'community.feed': '최신 글',
    'community.latest': '최신순',
    'community.posts': '개 글',
    'community.replies': '댓글',
    'community.topics': '주제',
    'community.noTopics': '아직 주제가 없습니다.',
    'community.empty': '아직 작성된 글이 없습니다.',
    'community.back': '커뮤니티',
    'community.comments': '댓글',
    'community.noComments': '아직 댓글이 없습니다.',
    'community.locked': '잠김',
    'community.space.general': '일반',
    'community.space.founder-notes': '글',
    'community.space.development-log': '개발 일지',
    'community.space.development': '개발',
	'community.space.operating-systems': '운영체제',
	'community.space.web': '웹',
	'community.space.compiler': '컴파일러',
	'community.space.audio': '오디오',
	'community.space.gui': 'GUI',
    'community.space.showcase': '쇼케이스',
    'community.space.help': '도움',
	'community.showcaseTitle': '쇼케이스',
	'community.showcaseEyebrow': '커뮤니티가 만든 결과물',
	'community.showcaseHeading': 'Wave로 만든 것들',
	'community.showcaseLead': 'Wave 커뮤니티 구성원이 만든 도구, 애플리케이션, 실험과 시스템을 둘러보세요.',
	'community.showcaseShare': '프로젝트 소개',
	'community.search': '글 검색',
	'community.createPost': '글 작성',
	'community.category': '분류',
	'community.postTitle': '제목',
	'community.body': '본문',
	'community.formatting': '서식 도구',
	'community.code': '코드 블록',
	'community.link': '링크',
	'community.quote': '인용',
	'community.imageUpload': '이미지 업로드',
	'community.imageUploading': '이미지를 WebP로 변환하는 중…',
	'community.imageHelp': '12 MiB 이하 JPEG, PNG, WebP · 최대 1920px로 축소 후 WebP 자동 변환',
	'community.imageUploadFailed': '이미지를 업로드하지 못했습니다.',
	'community.preview': '미리보기',
	'community.edit': '편집',
	'community.tags': '태그',
	'community.tagsPlaceholder': 'compiler, ffi, showcase',
	'community.englishOnly': '커뮤니티 게시글과 댓글은 영어로 작성해야 합니다. 코드 블록과 인라인 코드는 다른 언어를 포함할 수 있습니다.',
	'community.publish': '게시하기',
	'community.reply': '답글',
	'community.addComment': '댓글 작성',
	'community.replying': '답글 작성 중',
	'community.postComment': '댓글 등록',
	'community.signInToComment': '로그인하고 댓글 작성',
	'community.subscribe': '구독',
	'community.unsubscribe': '구독 중',
	'community.upvote': '추천',
	'community.downvote': '비추천',
	'community.sort': '글 정렬',
	'community.sort.latest': '최신',
	'community.sort.active': '최근 활동',
	'community.sort.top': '인기',
	'community.activity': '최근 활동',
	'community.popularTags': '인기 태그',
	'community.categories': '커뮤니티 현황',
	'community.visiblePosts': '표시된 글',
	'community.noSearchResults': '검색 조건에 맞는 글이 없습니다.',
	'community.actionFailed': '커뮤니티 요청을 처리하지 못했습니다.',
    'mail.title': '메일',
    'mail.lead': 'Wave 메일함과 메시지입니다.',
	'mail.empty': '이 메일함에 메시지가 없습니다.',
    'mail.compose': '메일 쓰기',
    'mail.inbox': '받은메일함',
    'mail.sent': '보낸메일함',
    'mail.drafts': '임시보관함',
    'mail.trash': '휴지통',
    'mail.folders': '메일함',
    'mail.select': '읽을 메시지를 선택하세요.',
    'mail.signInRequired': 'Wave 메일함을 열려면 로그인하세요.',
    'mail.signIn': 'Wave Mail 로그인',
	'mail.starred': '별표편지함',
	'mail.messages': '메시지',
	'mail.reader': '메일 읽기',
	'mail.selectMessage': '읽을 메시지를 선택하세요.',
    'mail.archive': '보관',
    'mail.delete': '삭제',
    'mail.markUnread': '읽지 않음',
    'mail.refresh': '새로고침',
    'mail.search': '메일 검색',
    'mail.emptyTitle': '받은 메일이 없습니다.',
    'mail.emptyDetail': '새 메일이 도착하면 여기에 표시됩니다.',
    'mail.noSearchResults': '검색 결과가 없습니다.',
    'mail.changeSearch': '검색어를 변경해 다시 시도하세요.',
    'mail.selectTitle': '메시지를 선택하세요',
    'mail.selectDetail': '메일 목록에서 메시지를 선택하면 이 영역에서 읽을 수 있습니다.',
    'mail.newMessage': '새 메일',
    'mail.to': '받는 사람',
    'mail.toPlaceholder': 'recipient@example.com',
    'mail.delivery.queued': '전송 대기',
    'mail.delivery.delivering': '전송 중',
    'mail.delivery.deferred': '재시도 중',
    'mail.delivery.delivered': '전달됨',
    'mail.delivery.failed': '전송 실패',
    'mail.subject': '제목',
    'mail.body': '메일 본문',
    'mail.sendMessage': '보내기',
    'mail.sendFailed': '메일을 보내지 못했습니다.',
	'mail.gmailWarning': 'Gmail은 Wave 메일을 거부하거나 지연할 수 있고 보안이 중요한 복구 메일에 적합하지 않을 수 있습니다. 가능하면 다음 서비스를 사용해 주세요:',
    'mail.actionFailed': '메일함 작업을 완료하지 못했습니다.',
	'mail.personal': '개인 메일',
	'mail.personalDetail': '내 Wave 개인 메일함',
	'mail.team': '팀 메일',
	'mail.teamDetail': '소유자와 관리자가 함께 처리하는 운영 메일',
	'mail.newTeamMessage': '새 팀 메일',
	'mail.reply': '답장',
	'mail.hubModes': '메일 영역',
	'mail.lists.title': '메일링 리스트',
	'mail.lists.modeDetail': 'Wave Mail 안에서 진행하는 내부 프로젝트 토론',
	'mail.lists.internalOnly': '활성 Wave 계정만 이용할 수 있습니다.',
	'mail.lists.directory': '메일링 리스트 목록',
	'mail.lists.subscribed': '구독 중',
	'mail.lists.threads': '토론 목록',
	'mail.lists.join': '가입',
	'mail.lists.leave': '탈퇴',
	'mail.lists.newThread': '새 토론',
	'mail.lists.patchReview': '패치 검토함',
	'mail.lists.patchSubjectPlaceholder': '[PATCH] compiler: 변경 내용',
	'mail.lists.search': '이 목록 검색',
	'mail.lists.messages': '개 메시지',
	'mail.lists.emptyTitle': '아직 토론이 없습니다.',
	'mail.lists.emptyDetail': '이 메일링 리스트의 첫 토론을 시작해 보세요.',
	'mail.lists.joinToPost': '토론을 시작하거나 답장하려면 이 목록에 가입하세요.',
	'mail.lists.threadReader': '메일링 리스트 토론',
	'mail.lists.subscriptionFailed': '구독 상태를 변경하지 못했습니다.',
	'mail.lists.postFailed': '토론을 발행하지 못했습니다.',
	'mail.lists.replyFailed': '답장을 보내지 못했습니다.',
	'mail.lists.publish': '토론 발행',
	'mail.lists.reply': '이 토론에 답장',
	'mail.lists.sendReply': '답장 보내기',
	'mail.lists.selectTitle': '토론을 선택하세요',
	'mail.lists.selectDetail': '목록에서 토론을 골라 이곳에서 읽고 답장할 수 있습니다.',
    'questions.title': '질문',
    'questions.lead': 'Wave에 관해 질문하고 채택된 답변을 찾습니다.',
    'questions.empty': '아직 공개된 질문이 없습니다.',
    'questions.search': '질문 검색',
    'questions.ask': '질문하기',
    'questions.newest': '최신 질문',
    'questions.unanswered': '답변 없는 질문',
    'questions.active': '활발한 질문',
    'questions.filters': '질문 필터',
    'questions.tags': '인기 태그',
    'questions.guide': '질문 전에 문서를 확인하고 사용 중인 Wave 버전을 적어주세요.',
    'questions.votes': '투표',
    'questions.answers': '답변',
    'questions.views': '조회',
    'questions.status.open': '답변 대기',
    'questions.status.answered': '답변 있음',
    'questions.status.resolved': '해결됨',
    'questions.status.closed': '닫힘',
    'questions.questionTitle': '제목',
    'questions.details': '질문 내용',
    'questions.tagsLabel': '태그',
    'questions.tagsPlaceholder': 'compiler, generics, linux',
    'questions.waveVersion': 'Wave 버전',
    'questions.platform': '플랫폼',
    'questions.publish': '질문 등록',
    'questions.vote': '투표',
    'questions.accept': '이 답변 채택',
    'questions.accepted': '채택된 답변',
    'questions.yourAnswer': '답변 작성',
    'questions.postAnswer': '답변 등록',
    'questions.toAnswer': '후 답변을 작성할 수 있습니다.',
    'questions.createFailed': '질문을 등록하지 못했습니다.',
    'questions.answerFailed': '답변을 등록하지 못했습니다.',
    'questions.voteFailed': '투표를 저장하지 못했습니다.',
    'questions.acceptFailed': '답변 채택 상태를 변경하지 못했습니다.',
    'source.title': '소스',
    'source.lead': '공식 컴파일러와 표준 라이브러리 소스를 탐색합니다.',
    'source.repositories': '저장소',
	'source.sections': '소스 메뉴',
	'patches.title': '패치',
	'patches.lead': 'Wave 계정으로 제출한 Git 패치를 검토하는 내부 공간입니다.',
	'patches.submit': '이메일로 패치 제출',
	'patches.submitHelp': 'Wave 계정에서 유효한 Git 패치 메시지를 제출하세요. 검토함은 프로젝트 회원만 볼 수 있습니다.',
	'patches.search': '패치, 작성자, 변경 파일 검색',
	'patches.files': '개 파일',
	'patches.empty': '검색 조건에 맞는 공개 패치가 없습니다.',
	'patches.notFound': '패치를 찾을 수 없습니다.',
	'patches.back': '전체 패치',
	'patches.changedFiles': '변경 파일',
	'patches.review': '메인테이너 검토',
	'patches.reviewHelp': '공식 검토 기능은 창시자와 지정된 Source Maintainer만 사용할 수 있습니다. 서버는 패치를 직접 적용하지 않습니다.',
	'patches.reviewStatus': '검토 상태',
	'patches.targetRepository': '대상 저장소',
	'patches.targetRepositoryPlaceholder': 'wavefnd/Wave',
	'patches.assignee': '담당 메인테이너',
	'patches.checksum': '원본 SHA-256',
	'patches.downloadMbox': 'mbox 다운로드',
	'patches.downloadSeries': '패치 시리즈 mbox 다운로드',
	'patches.saveReview': '검토 저장',
	'patches.reviewSaved': '패치 검토 상태를 저장했습니다.',
	'patches.status.received': '접수',
	'patches.status.reviewing': '검토 중',
	'patches.status.accepted': '승인',
	'patches.status.rejected': '거절',
	'patches.status.applied': '적용 완료',
	'patches.comments': '개 댓글',
	'patches.generalComments': '검토 토론',
	'patches.noComments': '아직 검토 댓글이 없습니다.',
	'patches.addGeneralComment': '전체 검토 댓글 추가',
	'patches.addComment': '댓글 추가',
	'patches.commentSaved': '검토 댓글을 추가했습니다.',
	'patches.commentResolved': '검토 댓글을 해결 처리했습니다.',
	'patches.commentReopened': '검토 댓글을 다시 열었습니다.',
	'patches.resolved': '해결됨',
	'patches.resolve': '해결 처리',
	'patches.reopen': '다시 열기',
	'patches.diff': '인라인 검토 댓글이 포함된 패치 diff',
	'patches.commentOnLine': '{line}번째 줄에 댓글 작성',
	'patches.commentingOn': '{path} 패치 {line}번째 줄에 댓글 작성',
	'rfc.title': 'RFC 제안서',
	'rfc.lead': 'Wave 언어와 플랫폼의 중요한 변경을 구현 전에 공개적으로 검토하는 공식 제안 공간입니다.',
	'rfc.all': '전체 RFC',
	'rfc.back': 'RFC로 돌아가기',
	'rfc.new': '새 RFC',
	'rfc.create': '제안서 작성',
	'rfc.edit': '제안서 수정',
	'rfc.editorHelp': '변경 배경, 구체적인 설계, 호환성 영향, 보안 고려사항과 대안을 기록하세요. 초안도 공개됩니다.',
	'rfc.proposalTitle': '제안 제목',
	'rfc.content': '제안서 본문 (Markdown)',
	'rfc.search': 'RFC, 작성자, 제안 내용 검색',
	'rfc.filterStatus': '상태 필터',
	'rfc.allStatuses': '전체 상태',
	'rfc.empty': '조건에 맞는 RFC가 없습니다.',
	'rfc.by': '{author} 제안',
	'rfc.officialStatus': '공식 상태',
	'rfc.updateStatus': '결정 기록',
	'rfc.confirmStatus': '이 RFC의 공식 상태를 “{status}”로 변경할까요? 결정은 감사 로그에 기록됩니다.',
	'rfc.statusSaved': 'RFC 공식 상태를 변경했습니다.',
	'rfc.discussion': '공개 토론',
	'rfc.discussionHelp': '설계와 그 영향을 논의하세요. 구현 세부사항도 제안과 연결해 기록합니다.',
	'rfc.noComments': '아직 토론 댓글이 없습니다.',
	'rfc.yourComment': '댓글',
	'rfc.addComment': '댓글 추가',
	'rfc.toComment': '후 RFC 토론에 참여할 수 있습니다.',
	'rfc.commentSaved': 'RFC 댓글을 추가했습니다.',
	'rfc.commentFailed': 'RFC 댓글을 저장하지 못했습니다.',
	'rfc.saveFailed': 'RFC를 저장하지 못했습니다.',
	'rfc.status.draft': '초안',
	'rfc.status.discussion': '논의 중',
	'rfc.status.accepted': '채택',
	'rfc.status.rejected': '거절',
	'rfc.status.implementing': '구현 중',
	'rfc.status.completed': '완료',
	'rfc.status.withdrawn': '철회',
    'source.searchRepositories': '저장소 검색',
    'source.code': '코드',
    'source.commits': '커밋',
    'source.tags': '태그',
    'source.name': '이름',
    'source.updated': '최근 변경',
    'source.projects': '저장소',
    'source.compilerDescription': 'Wave 언어 컴파일러와 표준 라이브러리입니다.',
    'source.vexDescription': 'Wave 프로젝트의 패키지 관리자와 빌드 도구입니다.',
    'source.whaleDescription': '어셈블러, 오브젝트, 링커와 IR을 제공하는 저수준 툴체인입니다.',
    'source.languages': '감지된 언어',
    'source.detected': '저장소 파일에서 감지됨',
    'source.allLanguages': '전체',
    'source.filterLanguage': '언어별 필터',
    'source.primaryLanguage': '주 언어',
    'source.openProject': '저장소 열기',
    'source.public': '공개',
    'source.mirror': '미러',
    'source.about': '저장소 정보',
    'source.lastCommit': '최근 커밋',
    'source.lastUpdated': '최근 변경',
    'source.branches': '브랜치',
    'source.branch': '브랜치',
    'source.tag': '태그',
    'source.commit': '커밋',
    'source.reference': '브랜치 또는 태그',
    'source.history': '기록',
    'source.openFile': '파일 열기',
    'source.changedFiles': '개 변경 파일',
    'source.diffTruncated': 'Diff가 너무 커서 일부만 표시합니다.',
    'source.path': '저장소 경로',
    'source.binaryFile': '바이너리 파일은 표시할 수 없습니다.',
    'admin.title': '관리',
	'admin.blog': '블로그',
	'admin.webhooks': '웹후크',
	'admin.webhookConfiguration': '웹후크 설정',
	'admin.webhookHelp': '커뮤니티·LunaStev 새 글, 릴리즈와 패치 이벤트를 Discord 또는 일반 HTTPS 엔드포인트로 전송합니다.',
	'admin.newWebhook': '새 웹후크',
	'admin.webhookName': '이름',
	'admin.webhookKind': '형식',
	'admin.webhookUrl': '대상 URL',
	'admin.webhookUrlRetained': '비워 두면 암호화된 기존 URL을 유지합니다.',
	'admin.webhookEvents': '이벤트',
	'admin.rotateWebhookSecret': '서명 비밀키 교체',
	'admin.webhookSecretOnce': '이 서명 비밀키는 다시 표시되지 않으므로 지금 복사하세요.',
	'admin.webhookEndpoints': '등록된 엔드포인트',
	'admin.webhookDeliveries': '최근 전달 기록',
	'admin.testWebhook': '시험 전송',
	'admin.confirmDeleteWebhook': '이 웹후크를 삭제할까요? 전달 이력과 감사 기록은 유지됩니다.',
	'admin.noWebhooks': '등록된 웹후크 엔드포인트가 없습니다.',
	'admin.noWebhookDeliveries': '기록된 웹후크 전달이 없습니다.',
	'admin.blogHelp': '릴리즈, Wave 공식 글과 순서를 지정한 버전 로드맵을 Markdown으로 작성합니다.',
	'admin.blogPosts': '블로그 글',
	'admin.newPost': '새 글',
	'admin.editPost': '글 수정',
	'admin.blogSlug': 'URL 슬러그',
	'admin.blogTitle': '제목',
	'admin.blogCategory': '분류',
	'admin.roadmapStatus': '로드맵 상태',
	'admin.selectRoadmapStatus': '상태 선택',
	'admin.roadmapOrder': '표시 순서',
	'admin.roadmapOrderHelp': '숫자가 작을수록 먼저 표시됩니다. 목표 출시일은 순서에 영향을 주지 않습니다.',
	'admin.targetReleaseDate': '목표 출시일',
	'admin.blogSummary': '요약',
	'admin.blogContent': 'Markdown 본문',
	'admin.blogStatus': '발행 상태',
	'admin.blogPreview': '미리보기',
	'admin.draft': '초안',
	'admin.published': '발행됨',
	'admin.blogSaved': '블로그 글을 저장했습니다.',
	'admin.noBlogPosts': '작성된 블로그 글이 없습니다.',
    'admin.lead': '실행 중인 Wave Platform의 런타임과 서비스 상태입니다.',
    'admin.modules': '모듈',
    'admin.overview': '개요',
    'admin.platform': '플랫폼',
    'admin.platformOverview': '플랫폼 개요',
    'admin.accounts': '계정',
    'admin.messagesToday': '오늘 메시지',
    'admin.gitMirrors': 'Git 미러',
    'admin.backToPlatform': 'Wave Platform',
    'admin.module': '모듈',
    'admin.status': '상태',
    'admin.availability': '사용 여부',
    'admin.enabled': '사용',
    'admin.disabled': '중지',
    'admin.management': '관리 영역',
    'admin.accountManagement': '계정 관리',
    'admin.accountManagementHelp': '계정을 검색하고 접근 권한과 상태를 관리합니다.',
    'admin.account': '계정',
    'admin.searchAccounts': '계정 검색',
    'admin.role': '역할',
    'admin.owner': '소유자',
    'admin.administrator': '관리자',
	'admin.sourceMaintainer': 'Source 메인테이너',
	'admin.rfcMaintainer': 'RFC 메인테이너',
    'admin.member': '회원',
    'admin.actions': '작업',
    'admin.suspended': '정지',
    'admin.suspend': '정지',
    'admin.restore': '복구',
    'admin.makeAdmin': '관리자로 지정',
    'admin.removeAdmin': '관리자 해제',
	'admin.makeSourceMaintainer': 'Source 메인테이너 지정',
	'admin.removeSourceMaintainer': 'Source 메인테이너 해제',
	'admin.makeRFCMaintainer': 'RFC 메인테이너 지정',
	'admin.removeRFCMaintainer': 'RFC 메인테이너 해제',
    'admin.recoveryVerified': '복구 이메일 확인됨',
    'admin.recoveryUnverified': '복구 이메일 미확인',
    'admin.noAccounts': '검색 조건에 맞는 계정이 없습니다.',
    'admin.mailQueue': '메일 발송 큐',
    'admin.mailAttention': '확인이 필요한 메일',
    'admin.recentDeliveries': '최근 발송 50건',
    'admin.queued': '대기',
    'admin.delivering': '발송 중',
    'admin.deferred': '지연',
    'admin.failed': '실패',
    'admin.delivered': '전달 완료',
    'admin.recipient': '수신자',
    'admin.destination': '대상',
    'admin.attempts': '시도',
    'admin.updated': '최근 상태',
    'admin.noDeliveries': '기록된 메일 발송이 없습니다.',
    'admin.syncEvery': '자동 동기화 주기',
    'admin.repository': '저장소',
    'admin.branch': '브랜치',
    'admin.head': 'HEAD',
    'admin.lastFetched': '최근 갱신',
    'admin.security': '인증 및 보안',
    'admin.totpEnrollment': 'TOTP 등록',
    'admin.verifiedRecovery': '확인된 복구 이메일',
    'admin.registration': '회원가입',
    'admin.open': '열림',
    'admin.closed': '닫힘',
    'admin.system': '시스템 및 저장소',
    'admin.databaseHealth': '데이터베이스 상태',
    'admin.lsmStorage': 'Badger LSM',
    'admin.valueLog': 'Badger 값 로그',
    'admin.totalStorage': '전체 데이터 파일',
    'admin.storageUsed': '저장소 사용량',
    'admin.ready': '정상',
    'admin.auditLog': '감사 로그',
    'admin.recentEvents': '최근 이벤트 100건',
    'admin.time': '시각',
    'admin.actor': '수행자',
    'admin.action': '작업',
    'admin.resource': '대상 리소스',
    'admin.result': '결과',
    'admin.noAuditEvents': '기록된 감사 이벤트가 없습니다.',
    'admin.refresh': '새로고침',
    'admin.confirmAction': '관리 작업 확인',
    'admin.confirmSuspend': '이 계정을 정지하고 활성 세션을 모두 폐기할까요?',
    'admin.confirmRestore': '이 계정의 접근 권한을 복구할까요?',
    'admin.confirmMakeAdmin': '이 계정에 플랫폼 관리자 권한을 부여할까요?',
    'admin.confirmRemoveAdmin': '플랫폼 관리자 권한을 해제하고 활성 세션을 폐기할까요?',
	'admin.confirmMakeSourceMaintainer': '이 계정에 Git 패치 공식 검토와 다운로드 권한을 부여할까요? 관리자 권한은 부여되지 않습니다.',
	'admin.confirmRemoveSourceMaintainer': '이 계정의 Git 패치 검토와 다운로드 권한을 해제할까요?',
	'admin.confirmMakeRFCMaintainer': '이 계정에 RFC 공식 결정 기록 권한을 부여할까요? 관리자 권한은 부여되지 않습니다.',
	'admin.confirmRemoveRFCMaintainer': '이 계정의 RFC 결정 권한을 해제할까요?',
    'admin.confirm': '확인',
    'admin.actionFailed': '관리 작업을 완료하지 못했습니다.',
	'admin.overviewLead': '운영 요약입니다. 상세 데이터와 제어 기능은 각 관리 영역에서 확인하세요.',
	'admin.managementMailbox': '관리자 통합 메일함',
	'admin.managementMailboxEmpty': '관리자 통합 메일함에 도착한 메일이 없습니다.',
	'admin.lunaStevTimeZone': 'LunaStev 표시 시간대',
	'admin.lunaStevTimeZoneHelp': 'LunaStev에만 적용하며 일반 커뮤니티 시간 표시는 변경하지 않습니다.',
	'user.directory': '사용자 디렉터리',
	'user.people': '사용자',
	'user.allUsers': '전체 사용자',
	'user.profile': '프로필',
	'user.joined': '가입',
	'user.editProfile': '프로필 수정',
	'user.profileSettings': '프로필 설정',
	'user.bio': '소개',
	'user.timeZone': '시간대',
	'user.waveAddress': 'Wave 메일 식별자',
	'user.localPart': '메일 주소',
	'user.addressIdentityHelp': 'Wave 메일 주소는 공개 고유 식별자입니다. 변경하려면 인증 앱 코드가 필요합니다.',
	'user.changeAddress': '메일 주소 변경',
	'user.profileSaved': '프로필 설정을 저장했습니다.',
	'user.addressSaved': 'Wave 메일 주소를 변경했습니다.',
	'user.updateFailed': '프로필을 수정하지 못했습니다.',
	'user.activity': '활동',
	'user.activity.all': '전체',
	'user.activity.community-post': '글',
	'user.activity.community-comment': '댓글',
	'user.activity.question': '질문',
	'user.activity.answer': '답변',
	'user.noActivity': '아직 공개 활동이 없습니다.',
    'common.loadError': '데이터를 불러오지 못했습니다.',
    'common.loading': '불러오는 중…',
	'common.enabled': '사용 중',
	'common.disabled': '사용 안 함',
    'common.retry': '다시 시도',
    'common.more': '더보기',
	'common.cancel': '취소',
	'common.save': '저장',
	'common.edit': '수정',
	'common.delete': '삭제',
	'common.close': '닫기',
	'mail.from': '보낸 사람',
    'common.skipContent': '본문으로 건너뛰기',
    'notFound.title': '페이지를 찾을 수 없습니다',
    'notFound.lead': '요청한 페이지가 존재하지 않습니다.',
    'notFound.home': '홈으로 돌아가기',
    'login.title': '로그인',
    'register.title': 'Wave 계정 만들기',
  },
}

function setLocale(nextLocale: Locale) {
  locale.value = nextLocale
  localStorage.setItem('wave-locale', nextLocale)
  document.documentElement.lang = nextLocale
}

document.documentElement.lang = locale.value

export function useI18n() {
  const t = (key: string, values: Record<string, string | number> = {}) => {
    const value = messages[locale.value][key] ?? messages.en[key] ?? key
    return value.replace(/\{([^{}]+)\}/g, (match, name: string) => name in values ? String(values[name]) : match)
  }
  return {
    locale: computed(() => locale.value),
    setLocale,
    t,
  }
}
